// The first board, from the answer to "What do you want to use this for?" (Features 4, 26 Oct). Each starter is a
// board of the right kind, set out with headings and widgets that say how they fill up, shown whole with the chat
// shut (Fixes 12). Nothing on a starter is a number nobody sent: a widget a service fills
// waits for it with its own first step (shared/fits.ts), and what works without any agent (tasks, notes, a clock,
// a timer, events, deals and posts typed by hand) works at once.
//
// The board is made on the server when the account starts (WorkspaceDO.init), so the first thing that opens is it.

import { type StarterId } from "./onboarding";
import type { BoardKind, ThemeSettings } from "./types";
import type { TemplateInk, TemplateWidget } from "./templates";
import { SHELF_W } from "./pipeline";

export interface StarterSpec {
  id: StarterId;
  /** the answer, on its card */
  label: string;
  /** one line under it: what the board is for */
  line: string;
  kind: BoardKind;
  /** the board's name and emoji */
  name: string;
  emoji: string;
  theme: ThemeSettings;
  /** on a canvas, where each sits; on a board with a side column (a content calendar, a pipeline), its widgets in order */
  widgets: TemplateWidget[];
  /** words in the board's hand, on a canvas */
  ink?: TemplateInk[];
}

/** A line in the board's hand: what a part of the board is and how it fills up. */
const hand = (x: number, y: number, w: number, text: string): TemplateInk => ({ x, y, w, h: 40, ink: { t: "text", text, color: "auto", fontSize: 21, font: "hand", opacity: 0.82 } });

/** Widgets in a board's side column, one under the other, as the column lays them out. */
function column(list: Omit<TemplateWidget, "x" | "y" | "w">[]): TemplateWidget[] {
  let y = 0;
  return list.map((w) => {
    const at = { ...w, x: 0, y, w: SHELF_W };
    y += w.h + 16;
    return at;
  });
}

export const STARTERS: StarterSpec[] = [
  {
    id: "business",
    label: "Track my business",
    line: "Revenue, visitors and your week on one dashboard.",
    kind: "canvas",
    name: "My business",
    emoji: "📈",
    theme: { id: "meadow" },
    widgets: [
      { type: "heading", title: "Heading", x: 0, y: 0, w: 192, h: 64, props: { text: "Money", align: "left" } },
      { type: "metric", title: "MRR", x: 0, y: 80, w: 288, h: 184, stream: "mrr", props: { compare: "auto", goodWhen: "up", spark: true } },
      { type: "chart", title: "MRR over time", x: 312, y: 80, w: 456, h: 264, stream: "mrr", props: { chart: "area", range: "90d", axis: true } },
      { type: "heading", title: "Heading", x: 0, y: 392, w: 192, h: 64, props: { text: "Visitors", align: "left" } },
      { type: "metric", title: "Visitors (30d)", x: 0, y: 472, w: 288, h: 184, stream: "visitors", props: { compare: "auto", goodWhen: "up", spark: true } },
      { type: "list", title: "Top pages", x: 312, y: 472, w: 456, h: 264, stream: "top-pages", props: { max: 6, bars: true } },
      { type: "heading", title: "Heading", x: 816, y: 0, w: 336, h: 64, props: { text: "This week", align: "left" } },
      { type: "tasks", title: "To do", x: 816, y: 80, w: 336, h: 264, props: { tasks: [] } },
      { type: "notes", title: "Focus", x: 816, y: 368, w: 336, h: 200, props: { text: "" } },
      { type: "clock", title: "Clock", x: 816, y: 592, w: 160, h: 144, props: { face: "digital", seconds: false } },
      { type: "timer", title: "Focus timer", x: 992, y: 592, w: 160, h: 144, props: { work: 25, rest: 5 }, style: { header: false } },
    ],
    ink: [
      hand(208, 14, 560, "fills in once you connect Stripe or Lemon Squeezy ↓"),
      hand(208, 406, 560, "fills in once you connect Plausible ↓"),
      hand(816, 752, 336, "works now, no setup ↑"),
    ],
  },
  {
    id: "build",
    label: "Plan a build with my coding agent",
    line: "Plan it step by step, then watch Claude Code or Codex build it.",
    kind: "plan",
    name: "My build plan",
    emoji: "🗺️",
    theme: { id: "paper" },
    widgets: [],
  },
  {
    id: "content",
    label: "Plan my content",
    line: "Every post on a calendar, drafted with your agent.",
    kind: "calendar",
    name: "Content calendar",
    emoji: "🗓️",
    theme: { id: "daydream" },
    widgets: column([
      { type: "notes", title: "What I post about", h: 216, props: { text: "" } },
      { type: "metric", title: "Subscribers", h: 184, stream: "subscribers", props: { compare: "auto", goodWhen: "up", spark: true } },
    ]),
  },
  {
    id: "clients",
    label: "Run my clients",
    line: "Deals from first hello to won, with follow-ups.",
    kind: "pipeline",
    name: "Clients",
    emoji: "🤝",
    theme: { id: "studio" },
    // the pipeline's own column: what is open and what was won this quarter (pipelineShelf); MRR once mrr has data
    widgets: [],
  },
  {
    id: "day",
    label: "Organize my day",
    line: "Your meetings and tasks on one calendar.",
    // the Calendar board took over the planner's job (Features 6, 30 Oct); it holds no widgets
    kind: "agenda",
    name: "My calendar",
    emoji: "📅",
    theme: { id: "summit" },
    widgets: [],
  },
];

export const STARTER: Record<StarterId, StarterSpec> = Object.fromEntries(STARTERS.map((s) => [s.id, s])) as Record<StarterId, StarterSpec>;

export const isStarter = (x: unknown): x is StarterId => typeof x === "string" && Object.hasOwn(STARTER, x);
